import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import { useForm } from "react-hook-form";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import { z } from "zod";
import type { AffiliateUserRole } from "Types/types";

const RESOURCES = Resources.Components.Affiliate.LeadInvitation;

const getValidationSchema = (t: TFunction) =>
  z.object({
    email: z
      .email(t(Resources.Validation.InvalidEmail))
      .min(1, t(Resources.Validation.Required)),
  });

type LeadInvitationFormModel = z.infer<ReturnType<typeof getValidationSchema>>;

type Props = {
  invitedUserRole: AffiliateUserRole;
  isLoading: boolean;
  errors?: {
    email: { type: string; message: string };
  };
  onSubmit: (model: LeadInvitationFormModel) => void;
};

export const LeadInvitationForm = ({
  invitedUserRole,
  errors: initialErrors,
  isLoading,
  onSubmit,
}: Props) => {
  const { t } = useResource();
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<LeadInvitationFormModel>({
    resolver: zodResolver(getValidationSchema(t)),
    defaultValues: { email: "" },
    errors: initialErrors,
  });

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <FormInput
        autoFocus
        control={control}
        name="email"
        errors={errors}
        label={t(Resources.Common.Email)}
        type="email"
        valueTransformer={(v) => v.toLowerCase()}
      />
      <PrimaryButton
        type="submit"
        fullWidth
        variant="contained"
        color="info"
        disabled={isLoading}
        endIcon={<ForwardToInboxOutlinedIcon />}
      >
        {t(RESOURCES.SendInvitation[invitedUserRole])}
      </PrimaryButton>
    </Box>
  );
};
